import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import {
  SERVICE_TYPES,
  SERVICE_TYPE_LABELS,
  isHttpUrl,
  serviceTypeHasAccessLink,
} from '@bekebi/domain';
import { Button } from '../../components/button';
import { useCreateService } from './api';

/**
 * Add a service (POST /api/services, `services.manage`). The choices are exactly the backend's
 * service types; a public address may be given for a Website only (the API's rule). The API
 * validates everything again and decides.
 */
const createSchema = z
  .object({
    type: z.enum(SERVICE_TYPES, { message: 'Choose a service type.' }),
    accessUrl: z.string().trim().max(2048, 'That address is too long.'),
  })
  .superRefine((value, ctx) => {
    if (value.accessUrl === '') return;
    if (!serviceTypeHasAccessLink(value.type)) {
      ctx.addIssue({
        code: 'custom',
        path: ['accessUrl'],
        message: 'Only a Website has a public address.',
      });
      return;
    }
    if (!isHttpUrl(value.accessUrl)) {
      ctx.addIssue({
        code: 'custom',
        path: ['accessUrl'],
        message: 'Enter a full address starting with https:// or http://.',
      });
      return;
    }
    const url = new URL(value.accessUrl);
    if (url.username || url.password) {
      ctx.addIssue({
        code: 'custom',
        path: ['accessUrl'],
        message: 'The address must not contain a user name or password.',
      });
    }
  });
type CreateForm = z.input<typeof createSchema>;

export function ServiceCreatePage() {
  const create = useCreateService();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { accessUrl: '' },
  });
  const type = watch('type');
  const showAddress = type !== undefined && serviceTypeHasAccessLink(type);

  const onSubmit = handleSubmit((values) => {
    const accessUrl = values.accessUrl === '' ? null : values.accessUrl;
    create.mutate(
      { type: values.type, ...(serviceTypeHasAccessLink(values.type) ? { accessUrl } : {}) },
      {
        onSuccess: (service) =>
          void navigate(`/app/services/${service.id}`, { state: { created: service.name } }),
      },
    );
  });

  return (
    <div className="max-w-xl">
      <p className="text-sm">
        <Link
          to="/app/services"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Services
        </Link>
      </p>
      <h1 className="mt-2">Add service</h1>
      <form onSubmit={onSubmit} noValidate aria-label="Add service" className="mt-6 space-y-5">
        <fieldset>
          <legend className="text-sm font-medium text-fg-2">Service type</legend>
          <div className="mt-2 space-y-2">
            {SERVICE_TYPES.map((value) => (
              <label key={value} className="flex min-h-10 items-center gap-3 text-sm">
                <input type="radio" value={value} {...register('type')} className="size-4" />
                {SERVICE_TYPE_LABELS[value]}
              </label>
            ))}
          </div>
          {errors.type ? <p className="mt-1 text-sm text-danger">{errors.type.message}</p> : null}
        </fieldset>

        {showAddress ? (
          <div>
            <label htmlFor="service-address" className="block text-sm font-medium text-fg-2">
              Public address (optional)
            </label>
            <input
              id="service-address"
              type="url"
              inputMode="url"
              placeholder="https://"
              aria-invalid={errors.accessUrl ? 'true' : 'false'}
              className="mt-1 block w-full rounded-md border border-line-strong bg-surface px-3 py-2 font-mono text-sm"
              {...register('accessUrl')}
            />
            {errors.accessUrl ? (
              <p className="mt-1 text-sm text-danger">{errors.accessUrl.message}</p>
            ) : null}
          </div>
        ) : null}

        {create.isError ? (
          <p role="alert" className="text-sm text-danger">
            {create.error.message}
          </p>
        ) : null}

        <div className="flex gap-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add service'}
          </Button>
          <Link
            to="/app/services"
            className="inline-flex min-h-10 items-center rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-fg hover:bg-raised"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
